import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RotateCcw, Save, Eye, Upload, Trash2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLayout, FONTS } from "@/context/LayoutContext";
import { Button, Card, Chip, Field, Input, Loading, Page, PageHeader, Segmented, Switch } from "@/components/kit";

const PRESETS = [
  { name: "Sálvia (original)", brand: "#4E6E5D", accent: "#C77D20", surface: "#FFFFFF", surface_alt: "#F4F4F2", text: "#1C1C1A" },
  { name: "Terracota", brand: "#B4532A", accent: "#2F6F73", surface: "#FFFCF8", surface_alt: "#F5EEE6", text: "#2A1E17" },
  { name: "Oceano", brand: "#1F5F8B", accent: "#E07A2E", surface: "#FFFFFF", surface_alt: "#EEF3F7", text: "#13212C" },
  { name: "Vinho", brand: "#7A2E3A", accent: "#B8892B", surface: "#FFFDFC", surface_alt: "#F4EFEE", text: "#251517" },
  { name: "Grafite", brand: "#2B2D2F", accent: "#D9822B", surface: "#FFFFFF", surface_alt: "#F2F2F0", text: "#111111" },
];

const SECTIONS = [["chart", "Gráfico de evolução do CMV"], ["categories", "CMV por categoria"], ["price_alerts", "Alertas de preço"], ["comparison", "Comparativo semanal"], ["history", "Histórico semanal"]];

function ColorField({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 rounded-xl border p-2.5" style={{ borderColor: "var(--border)" }}>
      <input type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent" data-testid={testid} />
      <div className="flex-1 min-w-0"><div className="label">{label}</div><div className="caption tnum">{value}</div></div>
    </label>
  );
}

function ImageUpload({ kind, label, hint, value, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const form = new FormData();
    form.append("kind", kind);
    form.append("file", file);
    setBusy(true);
    try {
      const r = await api("/layout/image", { method: "POST", form });
      onChange(r.url);
      toast.success("Imagem enviada · clique em Salvar para aplicar a todos");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  const logo = kind === "logo";
  return (
    <div className="sm:col-span-2 flex flex-col gap-2" data-testid={`layout-${kind}-upload`}>
      <div className="label">{label}</div>
      <div className="flex items-center gap-4 rounded-xl border p-3" style={{ borderColor: "var(--border)" }}>
        <div className={`${logo ? "w-16 h-16" : "w-28 h-16"} rounded-lg overflow-hidden flex items-center justify-center shrink-0`} style={{ background: "var(--surface-alt)" }}>
          {value ? <img src={value} alt="" className={`w-full h-full ${logo ? "object-contain" : "object-cover"}`} data-testid={`layout-${kind}-preview`} /> : <ImageIcon size={20} style={{ color: "var(--muted)" }} />}
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <p className="caption">{hint}</p>
          <div className="flex flex-wrap gap-2">
            <input ref={ref} type="file" accept={logo ? "image/png,image/jpeg,image/webp,image/svg+xml" : "image/png,image/jpeg,image/webp"} className="hidden" onChange={pick} data-testid={`layout-${kind}-file-input`} />
            <Button size="sm" variant="secondary" icon={Upload} loading={busy} onClick={() => ref.current?.click()} data-testid={`layout-${kind}-upload-button`}>{value ? "Trocar imagem" : "Enviar imagem"}</Button>
            {value && <Button size="sm" variant="ghost" icon={Trash2} onClick={() => onChange("")} data-testid={`layout-${kind}-remove-button`}>Remover</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LayoutSettings() {
  const { layout, setPreview } = useLayout();
  const qc = useQueryClient();
  const [f, setF] = useState(null);
  useEffect(() => { if (layout && !f) setF(layout); }, [layout, f]);
  useEffect(() => { if (f) setPreview(f); }, [f, setPreview]);
  useEffect(() => () => setPreview(null), [setPreview]);
  useEffect(() => {
    if (document.getElementById("all-fonts")) return;
    const l = document.createElement("link");
    l.id = "all-fonts";
    l.rel = "stylesheet";
    l.href = `https://fonts.googleapis.com/css2?${FONTS.map((x) => `family=${encodeURIComponent(x)}:wght@600`).join("&")}&display=swap`;
    document.head.appendChild(l);
  }, []);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = useMutation({
    mutationFn: () => api("/layout", { method: "PUT", body: f }),
    onSuccess: (d) => { qc.setQueryData(["layout"], d); toast.success("Layout salvo para todos os usuários"); },
    onError: (e) => toast.error(e.message),
  });
  const reset = useMutation({
    mutationFn: () => api("/layout", { method: "DELETE" }),
    onSuccess: (d) => { qc.setQueryData(["layout"], d); setF(d); toast.success("Layout restaurado ao padrão"); },
    onError: (e) => toast.error(e.message),
  });
  if (!f) return <Loading />;
  return (
    <Page>
      <PageHeader title="Personalizar layout" back subtitle="As alterações aparecem na hora como prévia. Salve para aplicar a todos os usuários." />
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-5 items-start">
        <div className="flex flex-col gap-5 min-w-0">
          <Card className="flex flex-col gap-4" data-testid="layout-identity-section">
            <div className="h3">Identidade</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nome do app"><Input value={f.app_name} onChange={(e) => set("app_name")(e.target.value)} data-testid="layout-app-name-input" /></Field>
              <Field label="Frase da tela de login"><Input value={f.tagline} onChange={(e) => set("tagline")(e.target.value)} data-testid="layout-tagline-input" /></Field>
              <ImageUpload kind="logo" label="Logotipo" hint="Também vira o ícone na aba do navegador · PNG, JPG, WEBP ou SVG até 2 MB" value={f.logo_url} onChange={set("logo_url")} />
              <ImageUpload kind="login" label="Imagem de fundo do login" hint="Cobre a tela inteira de login · PNG, JPG ou WEBP até 6 MB, de preferência na horizontal" value={f.login_image} onChange={set("login_image")} />
            </div>
          </Card>
          <Card className="flex flex-col gap-4" data-testid="layout-colors-section">
            <div className="h3">Cores</div>
            <div className="scroll-x">
              {PRESETS.map((p) => (
                <button key={p.name} className="chip" onClick={() => setF({ ...f, ...p, name: undefined })} data-testid={`layout-preset-${p.name.split(" ")[0].toLowerCase()}`}>
                  <span className="w-4 h-4 rounded-full" style={{ background: p.brand }} /><span className="w-4 h-4 rounded-full -ml-2.5 border-2 border-white" style={{ background: p.accent }} />{p.name}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <ColorField label="Cor principal" value={f.brand} onChange={set("brand")} testid="layout-brand-color" />
              <ColorField label="Cor de destaque / alertas" value={f.accent} onChange={set("accent")} testid="layout-accent-color" />
              <ColorField label="Fundo dos cartões" value={f.surface} onChange={set("surface")} testid="layout-surface-color" />
              <ColorField label="Fundo da página" value={f.surface_alt} onChange={set("surface_alt")} testid="layout-surface-alt-color" />
              <ColorField label="Texto" value={f.text} onChange={set("text")} testid="layout-text-color" />
            </div>
            <div className="flex flex-col gap-2">
              <span className="label">Tema</span>
              <Segmented testid="layout-theme" value={f.theme} onChange={set("theme")} options={[{ value: "light", label: "Claro" }, { value: "dark", label: "Escuro" }, { value: "auto", label: "Automático" }]} />
            </div>
          </Card>
          <Card className="flex flex-col gap-4" data-testid="layout-type-section">
            <div className="h3">Tipografia e forma</div>
            <div className="flex flex-wrap gap-2">
              {FONTS.map((fo) => <Chip key={fo} active={f.font === fo} onClick={() => set("font")(fo)} data-testid={`layout-font-${fo.replace(/\s/g, "-").toLowerCase()}`}><span style={{ fontFamily: `"${fo}"` }}>{fo}</span></Chip>)}
            </div>
            <Field label={`Arredondamento dos cantos: ${f.radius}px`}>
              <input type="range" min="0" max="28" value={f.radius} onChange={(e) => set("radius")(Number(e.target.value))} className="w-full accent-[var(--brand)]" data-testid="layout-radius-slider" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2"><span className="label">Densidade</span><Segmented testid="layout-density" value={f.density} onChange={set("density")} options={[{ value: "comfortable", label: "Confortável" }, { value: "compact", label: "Compacta" }]} /></div>
              <div className="flex flex-col gap-2"><span className="label">Menu no computador</span><Segmented testid="layout-nav" value={f.nav} onChange={set("nav")} options={[{ value: "sidebar", label: "Lateral" }, { value: "topbar", label: "Superior" }]} /></div>
            </div>
          </Card>
          <Card className="flex flex-col gap-3" data-testid="layout-sections-section">
            <div className="h3">Seções do Painel</div>
            {SECTIONS.map(([k, label]) => (
              <div key={k} className="flex items-center gap-4">
                <span className="flex-1 text-sm font-semibold">{label}</span>
                <Switch on={f.sections[k] !== false} onChange={(v) => setF({ ...f, sections: { ...f.sections, [k]: v } })} data-testid={`layout-section-${k}`} />
              </div>
            ))}
          </Card>
        </div>
        <div className="flex flex-col gap-4 lg:sticky lg:top-6">
          <Card data-testid="layout-preview-card">
            <div className="flex items-center gap-2 mb-4"><Eye size={16} className="muted" /><span className="eyebrow">Prévia</span></div>
            <div className="flex items-center gap-3 mb-4">
              {f.logo_url ? <img src={f.logo_url} alt="" className="w-9 h-9 rounded-xl object-cover" /> : <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-extrabold" style={{ background: "var(--brand)" }}>{(f.app_name || "C")[0]}</div>}
              <div className="h3 truncate" data-testid="layout-preview-app-name">{f.app_name}</div>
            </div>
            <div className="rounded-xl p-4 text-white" style={{ background: "var(--brand)" }}>
              <div className="text-xs opacity-75">ÚLTIMA SEMANA</div>
              <div className="text-3xl font-extrabold mt-1">28,4%</div>
              <div className="text-xs opacity-80">CMV sobre faturamento</div>
            </div>
            <div className="flex gap-2 mt-4 flex-wrap"><span className="badge badge-success">Dentro da meta</span><span className="badge badge-warning">Acima da meta</span></div>
            <div className="flex gap-2 mt-4"><Button size="sm">Primário</Button><Button size="sm" variant="secondary">Secundário</Button></div>
            <div className="mt-4"><Input placeholder="Campo de texto" /></div>
          </Card>
          <Button icon={Save} loading={save.isPending} onClick={() => save.mutate()} data-testid="save-layout-button">Salvar layout</Button>
          <Button variant="secondary" onClick={() => setF(qc.getQueryData(["layout"]))} data-testid="discard-layout-button">Descartar alterações</Button>
          <Button variant="ghost" icon={RotateCcw} loading={reset.isPending} onClick={() => reset.mutate()} data-testid="reset-layout-button">Restaurar padrão</Button>
        </div>
      </div>
    </Page>
  );
}
